import { Inject, Component, ViewChild, AfterViewInit } from '@angular/core';
import { SplashScreen } from '@ionic-native/splash-screen';
import { StatusBar } from '@ionic-native/status-bar';
import { Nav, Platform, Content, Menu, Events, ModalController, LoadingController, Loading, Alert, PopoverController, ToastController, AlertController } from 'ionic-angular';

//Config
import { APP_CONFIG, AppConfig } from './app-config.module';

//i18n
import { TranslateService, TranslationChangeEvent } from '@ngx-translate/core';

//Providers
import { Storage } from '@ionic/storage';
import { SBSuiteManager } from '../modules/sbsuite/providers/sbsuite.manager';

//Events
import { SBSuiteEvents } from '../modules/sbsuite/models/sbsuite.events';

//Utils
import { IonVirtualKeyboardComponent } from '../modules/utils/ion-virtual-keyboard/ion-virtual-keyboard.component';

//Models
import { Product } from '../modules/sbsuite/models/product.model';
import { Operation } from '../modules/sbsuite/models/operation.model';

//Pages
import { ProductsModal } from '../pages/product/products.modal';
import { OperationModal } from '../pages/operation/operation.modal';
import { InternalsModal } from '../pages/auto-clocking/internals.modal';
import { SBSuiteException } from '../modules/sbsuite/models/sbsuite-exception.model';
import MapLocationModal from '../pages/maplocation/maplocation.modal';



/**
 * Componente principal de la aplicación SBSuite
 *
 * @export
 * @class MyApp
 * @implements {AfterViewInit}
 */
@Component({
  templateUrl: 'app.html'
})
export class MyApp implements AfterViewInit{

  /**
   * Contiene el control de navegación
   *
   * @type {Nav}
   * @memberof MyApp
   */
  @ViewChild(Nav) nav: Nav;


  /**
   * Contiene el componente Menu.
   *
   * @type {Menu}
   * @memberof MyApp
   */
  @ViewChild(Menu) menu: Menu;


  /**
   * Contiene el componente de contenido.
   *
   * @type {Content}
   * @memberof MyApp
   */
  @ViewChild(Content) content: Content;


  /**
   * Contiene la pàgina inicial.
   *
   * @type {*}
   * @memberof MyApp
   */
  rootPage: any = 'AutoClocking';


  /**
   * Contiene los strings de las traducciones.
   *
   * @type {*}
   * @memberof MyApp
   */
  texts: any = null;


  /**
   * Contiene el array de pàginas del menú lateral.
   *
   * @type {Array<{ title: string, component: any, icon?: string }>}
   * @memberof MyApp
   */
  pages: Array<{ title: string, component: any, icon?: string }>;


  /**
   * Contiene un booleano para indicar si la app se ha cargado.
   *
   * @type {Loading}
   * @memberof MyApp
   */
  currentLoading: Loading = null;


  /**
   * Crea una instancia de MyApp.
   *
   * @param {Events} _events //Control de eventos
   * @param {Platform} platform //Control de plataforma
   * @param {PopoverController} _popoverCtrl //Control de popovers
   * @param {ModalController} _modalCtrl //Control de modales
   * @param {ToastController} _toastCtrl //Control de "toast msg"
   * @param {AlertController} _alertCtrl //Control de alertas
   * @param {Storage} _storage //Gestor de almacenamiento local
   * @param {StatusBar} statusBar //Control de la barra de estado de la app (sólo app móbil)
   * @param {SplashScreen} splashScreen //Pantalla de inicio (solo app móbil)
   * @param {SBSuiteManager} sbSuiteManager //Gestor del SBSuite
   * @param {LoadingController} loadingCtrl //Control de cargas
   * @param {TranslateService} _translateService //Servicio de traducciones
   * @memberof MyApp
   */
  constructor(
    private _events: Events,
    public platform: Platform,
    private _popoverCtrl: PopoverController,
    private _modalCtrl: ModalController,
    private _toastCtrl: ToastController,
    private _alertCtrl: AlertController,
    private _storage: Storage,
    public statusBar: StatusBar,
    public splashScreen: SplashScreen,
    public sbSuiteManager: SBSuiteManager,
    private loadingCtrl: LoadingController,
    private _translateService: TranslateService,
    @Inject(APP_CONFIG) private config: AppConfig
  ) {

    //Subscribe los eventos de la app
    this.subscribeEvents();

    //Idioma por defecto
    this._translateService.setDefaultLang('es');

    //Definción de paginas
    this.pages = [
      { title: 'MENU_CLOCKING', component: 'AutoClocking', icon: 'clock' },
      { title: 'MENU_REPORT_EXTRA_HOURS', component: 'Page1', icon: 'browsers' }
    ];


    //i18n
    this.sbSuiteManager.getSettings().then((value)=>{
      this._translateService.use(value.lang);
    });
    this.getTranslate();
    this._translateService.onLangChange.subscribe((event: TranslationChangeEvent) => {
      this._translateService.setDefaultLang(event.lang);
      this.getTranslate();
    });
    //Funciones a cargar una vez la plataforma está lista
    this.platformReady();
  }


  /**
   * Hook al cargar la vista
   *
   * @memberof MyApp
   */
  ngAfterViewInit(){
    this.resize();

  }


  /**
   *  Código a ejecutar cuando la plataforma está lista
   *
   * @memberof MyApp
   */
  platformReady(): void{
    this.platform.ready().then(() => {
      //Auth
      this.sbSuiteManager.getStoredUser().then((value)=>{})

      //Language
      this.sbSuiteManager.getSettings().then((value)=>{
        this._translateService.setDefaultLang(value.lang);
        this._translateService.use(value.lang)
      });

     // Okay, so the platform is ready and our plugins are available.
     // Here you can do any higher level native things you might need.
     this.statusBar.styleDefault();
     this.splashScreen.hide();
   });

  }

  /**
   * Carga las traducciones necesarias en el componente.
   *
   * @memberof MyApp
   */
  getTranslate(): void{
    this._translateService.get([
      'INSERT_MO',
      'WAIT_PLEASE',
      'CANCEL',
      'CONTINUE',
      'CONTINUE_OF',
      'LAST_OF_DETECTED',
      'MO',
      'PRODUCT',
      'OPERATION',
      'CHANGE_MO',
      'INTERNAL_MO',
      'CURRENT_MO_DETECTED',
      'NO_PRODUCT_DETECTED',
      'NO_OPERATION_DETECTED',
      'REGISTER_UPDATED',
      'REGISTER_CREATED',
      'REGISTER_DELETED',
      'EMPTY_HISTORY',
      'STARTED_CLOCK_IN'

    ]).subscribe(res => {
      this.texts = res;
    });
  }

  /**
   * Subscripción a los eventos de SBSuite
   *
   * @memberof MyApp
   */
  subscribeEvents(){

     //LOGIN
     this._events.subscribe(SBSuiteEvents.EVENTS.LOGIN_INIT, (code, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.LOGIN_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.LOGIN_END, (code, time) => {
      this.sbSuiteManager.listaOfsInternas();
      setTimeout(()=>this.sbSuiteManager.getParams(),300);
      this.nav.setRoot('AutoClocking');
      this.dismissCurrentLoading();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.LOGIN_FAIL, (msg, time) => {
      this.dismissCurrentLoading();
      this.presentToast(msg);
    });

    //OPERARIO
    this._events.subscribe(SBSuiteEvents.EVENTS.CAMBIAR_OPERARIO_INIT, (code, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.CAMBIAR_OPERARIO_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CAMBIAR_OPERARIO_END, (code, time) => {
      this.dismissCurrentLoading();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CAMBIAR_OPERARIO_OFS, (code, time) => {
      this.dismissCurrentLoading();
      this.sbSuiteManager.getOfsOperario(this.sbSuiteManager.sbSuite.currentOperator,new Date().toLocaleDateString());
    });


    this._events.subscribe(SBSuiteEvents.EVENTS.CAMBIAR_OPERARIO_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.sbSuiteManager.sbSuite.manuallyOperator = '';
      if(this.nav.canGoBack()) this.nav.pop();
      this.presentToast(error)
    });

    //OF DETECTADA
    this._events.subscribe(SBSuiteEvents.EVENTS.CURRENT_OF_DETECTED, (of, time) => {
      this.presentToast(this.texts.CURRENT_MO_DETECTED  + ' ' + of.cod_of)
      this.sbSuiteManager.loadCurrentClockIn();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.LAST_OF_DETECTED, (of, time) => {
      this._presentMODetected();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.NO_OF_DETECTED, (of, time) => {
      this.presentVK();
    });



    //OF
    this._events.subscribe(SBSuiteEvents.EVENTS.CAMBIAR_OF_INIT, (of, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.CAMBIAR_OF_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CAMBIAR_OF_END, (of, time) => {
      this.presentToast(this.texts.STARTED_CLOCK_IN);
      this.dismissCurrentLoading();
      this.sbSuiteManager.loadCurrentClockIn();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CAMBIAR_OF_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //PRODUCTO
    this._events.subscribe(SBSuiteEvents.EVENTS.PRODUCTO_OF_INIT, (of, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.PRODUCTO_OF_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.PRODUCTO_OF_END, (clockIn, time) => {
      this.dismissCurrentLoading();
      if(clockIn ===  null) return;
      let products = clockIn.getRelatedProducts();
      if(!products.length){
        this.presentToast(this.texts.NO_PRODUCT_DETECTED);
        this.sbSuiteManager.sbSuite.restoreCurrentIfStarted();
        //this.presentVK();
      }
      else if(products.length == 1){
        let product = new Product(products[0]);
        if(clockIn == this.sbSuiteManager.sbSuite.getCurrent()){
          this.sbSuiteManager.sbSuite.currentProducts = [product];
          this.sbSuiteManager.getOpsProdOfDropdown(product.code);
        }
        else if(clockIn == this.sbSuiteManager.sbSuite.getManually()){
          this.sbSuiteManager.sbSuite.manuallyProducts = [product];
          this.sbSuiteManager.getOpsProdOfDropdown(product.code,'manually');
        }

      }
      else this.presentProductsModal(clockIn);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.PRODUCTO_OF_FAIL, (error, time) => {
      this.sbSuiteManager.sbSuite.restoreCurrentIfStarted();
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //OPERACION
    this._events.subscribe(SBSuiteEvents.EVENTS.OPERACION_PROD_INIT, (product, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.OPERACION_PROD_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.OPERACION_PROD_END, (clockIn, time) => {
      this.dismissCurrentLoading();
      if(clockIn === null) return;
      let operations = clockIn.getRelatedOperations();
      if(!operations.length){
        this.presentToast(this.texts.NO_OPERATION_DETECTED);
      }
      else if(operations.length == 1){
        let operation = new Operation(operations[0]);
        if(clockIn == this.sbSuiteManager.sbSuite.getCurrent()){
          this.sbSuiteManager.sbSuite.currentOperation = operation;
          //Cambia la OF directamente si se detecta una sola operación
          this.sbSuiteManager.cambiarOf(this.sbSuiteManager.sbSuite.currentOperation.code, this.sbSuiteManager.sbSuite.currentOperator);
        }
        if(clockIn == this.sbSuiteManager.sbSuite.getManually()) this.sbSuiteManager.sbSuite.manuallyOperation = operation;
      }
      else this.presentOperationModal(clockIn);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.OPERACION_PROD_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //OFS
    this._events.subscribe(SBSuiteEvents.EVENTS.OFS_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.OFS_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.OFS_END, (ofs, time) => {
      this.dismissCurrentLoading();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.OFS_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //OFs OPERARIO
    this._events.subscribe(SBSuiteEvents.EVENTS.HISTORY_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.HISTORY_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.HISTORY_END, (ofs, time) => {
      this.dismissCurrentLoading();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.HISTORY_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      //let error2 = this.texts.EMPTY_HISTORY;
     // this.presentToast(error2);
    });



    //CONTINUE/EXIT
    this._events.subscribe(SBSuiteEvents.EVENTS.CONTINUE_OF_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.CONTINUE_OF_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CONTINUE_OF_END, (ofs, time) => {
      this.dismissCurrentLoading();
      this.presentToast(this.texts.STARTED_CLOCK_IN);
      this.dismissCurrentLoading();
      this.sbSuiteManager.loadCurrentClockIn();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CONTINUE_OF_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.EXIT_OPERATOR_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.CONTINUE_OF_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.EXIT_OPERATOR_END, (ofs, time) => {
      this.dismissCurrentLoading();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.EXIT_OPERATOR_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //DROPDOWS
    this._events.subscribe(SBSuiteEvents.EVENTS.DROPDOWNS_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.DROPDOWNS_END);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.DROPDOWNS_END, (ofs, time) => {
      this.dismissCurrentLoading();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.DROPDOWNS_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //UPDATE
    this._events.subscribe(SBSuiteEvents.EVENTS.UPDATE_REG_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.UPDATE_REG_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.UPDATE_REG_END, (ofs, time) => {
      this.dismissCurrentLoading();
      //@TODO:MSG
      this.nav.pop().then(()=>{
        this.presentToast(this.texts.REGISTER_UPDATED);
      });
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.UPDATE_REG_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //CREATE
    this._events.subscribe(SBSuiteEvents.EVENTS.CREATE_REG_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.CREATE_REG_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CREATE_REG_END, (ofs, time) => {
      this.dismissCurrentLoading();
      this.nav.pop().then(()=>{
        this.presentToast(this.texts.REGISTER_CREATED);
      });

    });

    this._events.subscribe(SBSuiteEvents.EVENTS.CREATE_REG_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //DELETE
    this._events.subscribe(SBSuiteEvents.EVENTS.DELETE_REG_INIT, (operator, time) => {
      this.currentLoading = this.presentLoading(SBSuiteEvents.EVENTS.CREATE_REG_INIT);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.DELETE_REG_END, (ofs, time) => {
      this.dismissCurrentLoading();
      this.presentToast(this.texts.REGISTER_DELETED);
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.DELETE_REG_FAIL, (error, time) => {
      this.dismissCurrentLoading();
      this.presentToast(error);
    });

    //AUTH
    this._events.subscribe(SBSuiteEvents.EVENTS.AUTH_FAIL, (error, time) => {
      this.resize();
      this.presentToast(error);
    });
    this._events.subscribe(SBSuiteEvents.EVENTS.AUTH_END, (error, time) => {
      this.resize();
    });

    this._events.subscribe(SBSuiteEvents.EVENTS.APP_LOADED, (error, time) => {
      this.resize();
    }); 

    this._events.subscribe('map:createdStart', (data, time) => {
      this.presentMapLocationModal(data, 1);
    });

    this._events.subscribe('map:createdEnd', (data, time) => {
      this.presentMapLocationModal(data, 2);
    });
  }

  /**
   * Carga la página seleccionada des del menú lateral.
   *
   * @param {*} page
   * @memberof MyApp
   */
  openPage(page): void{
    // Reset the content nav to have just this page
    // we wouldn't want the back button to show in this scenario
    this.nav.setRoot(page.component);
  }

  /**
   * Abre la página de configuración de la app
   *
   * @memberof MyApp
   */
  openSettings(): void{
    this.nav.push('Settings');
  }


  /**
   * Realiza el logout de usuario.
   *
   * @memberof MyApp
   */
  logOut(): void{
    //@TODO: Implementar con el sistema de login
    this.sbSuiteManager.sbSuite.resetClocking();
    this.sbSuiteManager.resetStoredUser();
    this.nav.setRoot('Login');
  }

  logoChanged(){
    this.resize();
  }

  /**
   * Llama el método resize del componente "Content"
   *
   * @memberof MyApp
   */
  resize(): void{
    setTimeout(()=>{
      if(this.content) this.content.resize()
    });
  }


  /** UI FUNCTIONS **/


  /**
   * Abre el teclado virtual como Popover
   *
   * @memberof MyApp
   */
  presentVK(): void{//Si es un dispositivo SM no abre el popover
    if(window.innerWidth < 576){
      this._events.publish(SBSuiteEvents.EVENTS.OF_FOCUS,null,Date.now());
      return;
    }
    let prefixMask = new Date().getFullYear().toString().substr(-2);
    let mask = prefixMask + 'XXXX';
    let vkModal = this._popoverCtrl.create(
      IonVirtualKeyboardComponent,
      {
          input: null,
          layout: 'numericKeyboard',
          value: this.sbSuiteManager.sbSuite.clocking.current.manufacturingOrder,
          title: this.texts.INSERT_MO,
          mask: mask
      },
      {
          cssClass: 'virtual-keyboard-popover'
      }
    );

      //Transmit popover value
      vkModal.onDidDismiss((data,role) =>{
       if(data && typeof data.value != 'undefined') this.sbSuiteManager.getProductosOfDropdown(data.value);
      });
      vkModal.present();
  }

  /**
   * Muestra los mensajes de carga para peticiones asíncronas si está activada en la configuración.
   *
   * Devuelve el loader creado en el proceso.
   *
   * @param {string} [msg='WAIT_PLEASE'] Texto a mostrar
   * @param {number} [duration=SBSuiteEvents.EVENT_DURATION] Duración máxima del "loader"
   * @returns {Loading} El "Loader" creado
   * @memberof MyApp
   */
  presentLoading(msg: string = 'WAIT_PLEASE', duration: number = SBSuiteEvents.EVENT_DURATION): Loading {
    if(!this.config.showLoadings) return;
    msg = this.texts.WAIT_PLEASE;
    let loading: Loading = this.loadingCtrl.create({
      content: msg
    });
    if(duration) loading.setDuration(duration);
    loading.present();
    return loading;
  }

  /**
   * Muestra los mensajes de carga para peticiones asíncronas.
   *
   * Devuelve el loader creado en el proceso.
   *
   * @param {string} [msg='WAIT_PLEASE'] Texto a mostrar
   * @param {number} [duration=SBSuiteEvents.EVENT_DURATION] Duración máxima del "loader"
   * @returns {Loading} El "Loader" creado
   * @memberof MyApp
   */
  presentLoadingMandatory(msg: string = 'WAIT_PLEASE', duration: number = SBSuiteEvents.EVENT_DURATION): Loading {
    msg = this.texts.WAIT_PLEASE;
    let loading: Loading = this.loadingCtrl.create({
      content: msg
    });
    if(duration) loading.setDuration(duration);
    loading.present();
    return loading;
  }


  /**
   * Llama el método para cerrar el loader de la variable de classe
   *
   * @memberof MyApp
   */
  dismissCurrentLoading(){
    if(this.currentLoading){
      this.currentLoading.dismiss();
      this.currentLoading = null;
    }
  }


  /**
   * Abre el modal para seleccionar producto.
   *
   * @param {*} data
   * @memberof MyApp
   */
  presentProductsModal(data: any): void {
    let productsModal = this._modalCtrl.create(ProductsModal, {data:data});
    productsModal.onDidDismiss(data => {
      if(!data) return;
      if(typeof data.clockIn != 'undefined'){
        let type = 'auto';
        //Si la fichada és la manual pasamos el parámetro 'manually'
        if(data.clockIn == this.sbSuiteManager.sbSuite.getManually()) type = 'manually';
        this.sbSuiteManager.getOpsProdOfDropdown(data.clockIn.getProduct().code, type);
      }
      else{
        this.sbSuiteManager.sbSuite.restoreCurrentIfStarted();
      }
    });
    productsModal.present();
  }


  /**
   * Abre el modal para la selección de operación
   *
   * @param {*} data
   * @memberof MyApp
   */
  presentOperationModal(data: any): void {
    let operationModal = this._modalCtrl.create(OperationModal,{data:data});
    operationModal.onDidDismiss(data => {
      if(data && typeof data.clockIn != 'undefined'){
        //Si es la fichada actual ya tenemos todos los datos e iniciamos la OF
        if(data.clockIn == this.sbSuiteManager.sbSuite.getCurrent()){
          this.sbSuiteManager.cambiarOf(this.sbSuiteManager.sbSuite.currentOperation.code, this.sbSuiteManager.sbSuite.currentOperator);
        }
      }
      else{
          this.sbSuiteManager.sbSuite.restoreCurrentIfStarted();
      }

    });
    operationModal.present();
  }

  /**
   * Abre el modal para la selección de una OF interna.
   *
   * @memberof MyApp
   */
  openInternals(): void{
    let internalsModal = this._modalCtrl.create(InternalsModal);
    internalsModal.present();
  }

  /**
   * Muestra los mensajes "Toast" de la app.
   *
   * @param {string} [message='Error'] El mensaje a mostrar.
   * @memberof MyApp
   */
  presentToast(message: string = 'Error'): void {
    if(message == SBSuiteException.EXCEPTIONS.SESSION_INVALID || message == SBSuiteException.EXCEPTIONS.SESSION_EXPIRED){
      this.logOut();
    }

    let toast = this._toastCtrl.create({
      message: message,
      dismissOnPageChange: false,
      position: 'middle',
      duration: 5000,
      showCloseButton: true,
      closeButtonText: 'Ok'

    });
    toast.present();
  }



  /**
   * Alert para informar de una OF anterior.
   *
   * Permite continuar la OF detectada, ejecutar una nueva o una interna
   *
   * @private
   * @memberof MyApp
   */
  private _presentMODetected(): void{
    let alert: Alert = this._alertCtrl.create({
      cssClass: 'buttons-row',
      title: this.texts.CONTINUE_OF,
      subTitle: this.texts.LAST_OF_DETECTED + ':',
      message: `
          <ul>
            <li><b>${this.texts.MO}</b>: ${this.sbSuiteManager.sbSuite.getLast().manufacturingOrder}</li>
            <li><b>${this.texts.PRODUCT}</b>: ${this.sbSuiteManager.sbSuite.getLast().getProductsList()}</li>
            <li><b>${this.texts.OPERATION}</b>: ${this.sbSuiteManager.sbSuite.getLast().getOperationName()}</li>
          </ul>
        `,
      buttons: [
        {
          text: this.texts.CHANGE_MO,
          handler: data => {
            this.presentVK();
            return true;
          }
        },
        {
          text: this.texts.INTERNAL_MO,
          handler: data => {
            this.openInternals();
            return true;
          }
        },
        {
          text: this.texts.CONTINUE,
          handler: data => {
            this.sbSuiteManager.continueLastClockIn();
            return true;
          }
        }
      ]
    });
    alert.present();
  }

  presentMapLocationModal(data: any, tipo: any){
    let mapLocationModal = this._modalCtrl.create(MapLocationModal, {data:data, tipo:tipo});
   
    mapLocationModal.present();

    mapLocationModal.onDidDismiss(data => {
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/app.component.ts